import React from 'react';
import { Car } from 'lucide-react';

interface VehicleOwnership {
  isOwner: string;
  isKeeper: string;
  purchaseDate: string;
  notPurchased: boolean;
  parkingLocation: string;
}

interface VehicleOwnershipStepProps {
  data: VehicleOwnership;
  onInputChange: (field: string, value: string | boolean) => void;
}

export function VehicleOwnershipStep({ data, onInputChange }: VehicleOwnershipStepProps) {
  // Set default values when component mounts
  React.useEffect(() => {
    // Set default owner to Yes
    if (!data.isOwner || data.isOwner === '') {
      onInputChange('isOwner', 'Yes');
    }
    // Set default keeper to Yes
    if (!data.isKeeper || data.isKeeper === '') {
      onInputChange('isKeeper', 'Yes');
    }
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-6">
        <Car className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-semibold">Vehicle Ownership</h2>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Are you/will you be the registered owner of the car?
        </label>
        <div className="space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Yes"
              checked={data.isOwner === 'Yes'}
              onChange={(e) => onInputChange('isOwner', e.target.value)}
              className="rounded-full h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-2">Yes</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="No"
              checked={data.isOwner === 'No'}
              onChange={(e) => onInputChange('isOwner', e.target.value)}
              className="rounded-full h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-2">No</span>
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Are you/will you be the registered keeper of the car?
        </label>
        <div className="space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Yes"
              checked={data.isKeeper === 'Yes'}
              onChange={(e) => onInputChange('isKeeper', e.target.value)}
              className="rounded-full h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-2">Yes</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="No"
              checked={data.isKeeper === 'No'}
              onChange={(e) => onInputChange('isKeeper', e.target.value)}
              className="rounded-full h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-2">No</span>
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Has this car been purchased?
        </label>
        <div className="space-y-2">
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="yes"
                checked={!data.notPurchased}
                onChange={(e) => {
                  onInputChange('notPurchased', e.target.value !== 'yes');
                }}
                className="rounded-full h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                required
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="no"
                checked={data.notPurchased}
                onChange={(e) => {
                  onInputChange('notPurchased', e.target.value === 'no');
                  if (e.target.value === 'no') {
                    onInputChange('purchaseDate', '');
                  }
                }}
                className="rounded-full h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                required
              />
              <span className="ml-2">No</span>
            </label>
          </div>

          {!data.notPurchased && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                When was the car purchased?
              </label>
              <input
                type="date"
                value={data.purchaseDate || ''}
                onChange={(e) => onInputChange('purchaseDate', e.target.value)}
                className="w-full p-2 border rounded"
                max={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Where is the car parked overnight?
        </label>
        <select
          value={data.parkingLocation}
          onChange={(e) => onInputChange('parkingLocation', e.target.value)}
          className="w-full p-2 border rounded"
          required
        >
          <option value="">Select parking location</option>
          <option value="Garage">Private Garage</option>
          <option value="Driveway">Private Driveway</option>
          <option value="CarPort">Carport</option>
          <option value="Street">Street Outside Home</option>
          <option value="PublicParking">Public Parking</option>
          <option value="Other">Other</option>
        </select>
      </div>
    </div>
  );
}